import { ErrorBoundary } from "react-error-boundary";
import { Text, Title } from "../../tremor/Text";
import { Button } from "../../tremor/Button";
import { TableChart } from "../TableChart";
import React, { useMemo, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, Colors } from "chart.js";
import "chart.js/auto";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import "chartjs-adapter-spacetime";
import { MetricChart, MetricPlugin } from "../MetricChart";
import TextChart from "./TextChart";
import ImageChart from "./ImageChart";
import { DividerChart, DividerPlugin } from "./DividerChart";
import { FunnelController, TrapezoidElement, } from "chartjs-chart-funnel";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import { PolygonController, PolylineController, MarkerController, MapPlugin, MapController } from "@onvo-ai/chartjs-chart-map";
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { WordCloudController, WordElement } from 'chartjs-chart-wordcloud';
import { SankeyController, Flow } from 'chartjs-chart-sankey';
import { useDashboard } from "../../layouts/Dashboard/useDashboard";
import { useBackend } from "../../layouts";
function transparentize(hexColor, opacity) {
    // Convert opacity to hex value
    let alphaHex = Math.round(opacity * 255).toString(16);
    if (alphaHex.length === 1) {
        alphaHex = "0" + alphaHex;
    }
    // Return the hex color with the alpha value
    return hexColor + alphaHex;
}
function convertFunctionStringsToFunctions(config) {
    // Helper function to handle each item in the config
    const convertItem = (item) => {
        // If it's a string that looks like a function definition
        if (typeof item === 'string' && item.startsWith('function(')) {
            // Use the Function constructor to create an actual function
            try {
                // Extract the function body and parameters
                const funcBody = item.substring(item.indexOf('{') + 1, item.lastIndexOf('}'));
                const funcParams = item.substring(item.indexOf('(') + 1, item.indexOf(')')).split(',');
                // Create a new function using the Function constructor
                return new Function(...funcParams, funcBody);
            }
            catch (error) {
                console.error('Error converting function string to function:', error);
                return item; // Return original if conversion fails
            }
        }
        else if (Array.isArray(item)) {
            // If it's an array, process each element
            return item.map(convertItem);
        }
        else if (item !== null && typeof item === 'object' && typeof item !== 'function') {
            // If it's an object (and not a function itself), process each property
            const result = {};
            for (const key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    result[key] = convertItem(item[key]);
                }
            }
            return result;
        }
        // Return unchanged for all other types (including actual functions)
        return item;
    };
    // Start the conversion by directly passing config to convertItem.
    // convertItem itself will build a new, transformed object structure,
    // preserving actual functions and converting stringified ones.
    // This removes the problematic JSON.parse(JSON.stringify(config)) which stripped functions.
    return convertItem(config);
}
ChartJS.register(PolygonController, PolylineController, MarkerController, MapController, FunnelController, TrapezoidElement, MetricChart, MetricPlugin, TextChart, ImageChart, DividerPlugin, DividerChart, ChartDataLabels, zoomPlugin, WordCloudController, WordElement, SankeyController, Flow, MatrixController, MatrixElement, Colors);
const ChartBase = ({ json, title, pagination, stats, setPagination, padding }) => {
    const chartRef = useRef();
    const [zoomed, setZoomed] = useState(false);
    const theme = useTheme();
    const { dashboard } = useDashboard();
    const { googleMapsLoaded } = useBackend();
    const resetZoom = () => {
        var _a;
        (_a = chartRef.current) === null || _a === void 0 ? void 0 : _a.resetZoom();
        setZoomed(false);
    };
    let chartConfig = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
        if (!json) {
            return undefined;
        }
        let output = Object.assign({}, json, {});
        if (!output.options) {
            output.options = {};
        }
        if (!output.options.plugins) {
            output.options.plugins = {};
        }
        let subtitle = ((_b = (_a = output.options.plugins) === null || _a === void 0 ? void 0 : _a.subtitle) === null || _b === void 0 ? void 0 : _b.display) !== false &&
            ((_e = (_d = (_c = output.options.plugins) === null || _c === void 0 ? void 0 : _c.subtitle) === null || _d === void 0 ? void 0 : _d.text) === null || _e === void 0 ? void 0 : _e.trim) &&
            ((_h = (_g = (_f = output.options.plugins) === null || _f === void 0 ? void 0 : _f.subtitle) === null || _g === void 0 ? void 0 : _g.text) === null || _h === void 0 ? void 0 : _h.trim()) !== "";
        output.options.plugins.title = {
            display: (title || ((_j = output.options.plugins.title) === null || _j === void 0 ? void 0 : _j.text) || "").trim() === "" ? false : true,
            text: title || ((_k = output.options.plugins.title) === null || _k === void 0 ? void 0 : _k.text) || "",
            align: ((_l = output.options.plugins.title) === null || _l === void 0 ? void 0 : _l.align) || "start",
            position: ((_m = output.options.plugins.title) === null || _m === void 0 ? void 0 : _m.position) || "top",
            fullSize: true,
            font: {
                size: output.type === "text" ? 24 : 18,
                weight: 600,
            },
            color: theme === "dark" ? (((_o = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _o === void 0 ? void 0 : _o.dark_text) || "#ddd") : (((_p = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _p === void 0 ? void 0 : _p.light_text) || "#111"),
            padding: ((_q = output.options.plugins.title) === null || _q === void 0 ? void 0 : _q.padding) || {
                bottom: 5,
            },
        };
        if (subtitle) {
            output.options.plugins.subtitle = {
                display: ((_s = (_r = output.options.plugins) === null || _r === void 0 ? void 0 : _r.subtitle) === null || _s === void 0 ? void 0 : _s.display) || true,
                text: ((_t = output.options.plugins.subtitle) === null || _t === void 0 ? void 0 : _t.text) || "",
                align: ((_u = output.options.plugins.subtitle) === null || _u === void 0 ? void 0 : _u.align) || "start",
                position: ((_v = output.options.plugins.subtitle) === null || _v === void 0 ? void 0 : _v.position) || "top",
                fullSize: true,
                font: {
                    size: 12,
                    weight: 400,
                },
                padding: ((_w = output.options.plugins.subtitle) === null || _w === void 0 ? void 0 : _w.padding) || {
                    bottom: 10,
                },
            };
        }
        if (["line", "bar", "scatter"].indexOf(json.type) >= 0 && !((_x = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _x === void 0 ? void 0 : _x.disable_widget_interactions)) {
            output.options.plugins.zoom = {
                pan: {
                    enabled: true,
                    mode: output.type === "scatter" ? "xy" : "x",
                    modifierKey: "shift",
                },
                zoom: {
                    drag: {
                        enabled: true,
                        backgroundColor: "rgba(59, 130, 246, 0.2)",
                        borderColor: "rgb(59, 130, 246)",
                        borderWidth: 2,
                    },
                    pinch: {
                        enabled: true,
                    },
                    mode: output.type === "scatter" ? "xy" : "x",
                    onZoomComplete: ({ chart }) => {
                        setZoomed(true);
                    },
                    on: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }
                },
            };
        }
        if (output.type === "matrix") {
            output.data.datasets = output.data.datasets.map((dataset, index) => {
                return Object.assign(Object.assign({}, dataset), { backgroundColor: (context) => {
                        const value = context.dataset.data[context.dataIndex].v;
                        const max = Math.max(...dataset.data.map((d) => d.v));
                        const opacity = (value / max);
                        var alpha = opacity === undefined ? 0.5 : (1 - opacity);
                        return transparentize(dataset.baseColor || "#22c55e", alpha);
                    }, borderWidth: 2, borderColor: theme === "dark" ? "#0f172a" : "#ffffff", width: ({ chart }) => (chart.chartArea || {}).width / chart.scales.x.ticks.length, height: ({ chart }) => (chart.chartArea || {}).height / chart.scales.y.ticks.length });
            });
        }
        output.options.theme = theme;
        if (!output.options.layout) {
            output.options.layout = {};
        }
        output.options.layout.padding = padding;
        return output;
    }, [json, title, theme, dashboard]);
    const options = useMemo(() => chartConfig ? convertFunctionStringsToFunctions(chartConfig.options) : undefined, [chartConfig]);
    if (chartConfig && chartConfig.type === "map" && !googleMapsLoaded) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(ErrorBoundary, { fallbackRender: ({ error }) => (React.createElement("div", { className: "onvo-chart-base-error-fallback onvo-flex onvo-h-full onvo-w-full onvo-flex-col onvo-items-center onvo-justify-center" },
            React.createElement(Title, { className: "onvo-error-title" }, "Error rendering chart"),
            React.createElement(Text, { className: "onvo-error-message" }, error.message))) }, chartConfig ? (chartConfig.type === "table" ? (React.createElement(TableChart, { data: chartConfig, pagination: pagination, stats: stats, setPagination: setPagination, padding: padding })) : (React.createElement("div", { className: "onvo-relative onvo-w-full onvo-h-full onvo-flex onvo-flex-col" },
        React.createElement(Chart, Object.assign({ ref: chartRef, plugins: chartConfig.type === "map" ? [MapPlugin] : [], key: theme }, chartConfig, { options: options })),
        React.createElement("div", { className: "onvo-backdrop-blur-lg onvo-background-transparent onvo-shadow-xl onvo-border onvo-border-black/10 dark:onvo-border-white/10 onvo-absolute -onvo-bottom-5 onvo-left-[50%] onvo-w-[300px] -onvo-ml-[150px] onvo-overflow-y-hidden onvo-shrink-0 z-50 onvo-px-2 onvo-transition-all onvo-rounded-full onvo-flex onvo-flex-row onvo-items-center onvo-justify-between " +
                (zoomed ? "onvo-h-10 onvo-opacity-100 onvo-z-50" : "onvo-h-0 onvo-opacity-0 onvo-z-0") },
            React.createElement("div", { className: "onvo-w-full onvo-absolute onvo-top-0 onvo-left-0 onvo-right-0 onvo-bottom-0 onvo-foreground-color onvo-opacity-50 onvo-rounded-lg -onvo-z-10" }),
            React.createElement(Text, { className: "onvo-text-xs onvo-font-semibold ml-2" },
                "Hold",
                " ",
                React.createElement("span", { className: "onvo-rounded-[4px] onvo-px-1 onvo-py-0.5 onvo-border-solid onvo-border onvo-shadow-sm onvo-border-gray-200 onvo-bg-gray-100 dark:onvo-border-gray-600 dark:onvo-bg-gray-700 dark:onvo-text-gray-400" }, "Shift"),
                " ",
                "and drag to pan"),
            React.createElement(Button, { className: "!onvo-px-2 !onvo-py-0.5 !onvo-rounded-full !onvo-bg-blue-500 dark:!onvo-bg-blue-500 onvo-text-gray-600 dark:onvo-text-gray-300", onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    resetZoom();
                }, onMouseDown: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                } }, "Reset zoom"))))) : (React.createElement(React.Fragment, null))));
};
export default ChartBase;
//# sourceMappingURL=ChartBase.js.map